import Image from "next/image";
import { cn } from "@/lib/utils";

/**
 * Provider logo component (W3-1).
 *
 * Renders a real logo when `logoUrl` is provided, falling back to initials on
 * a deterministic brand-coloured background when the field is null/undefined.
 */

const PALETTE = [
  "bg-emerald-100 text-emerald-700",
  "bg-sky-100 text-sky-700",
  "bg-violet-100 text-violet-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-emerald-100 text-emerald-700",
  "bg-indigo-100 text-indigo-700",
  "bg-orange-100 text-orange-700",
] as const;

function pickColour(name: string): string {
  // Deterministic: sum char codes → stable index, no random flicker on SSR/CSR.
  let sum = 0;
  for (let i = 0; i < name.length; i++) {
    sum += name.charCodeAt(i);
  }
  return PALETTE[sum % PALETTE.length];
}

function getInitials(name: string): string {
  // Strip common legal suffixes that would pollute initials.
  const cleaned = name
    .replace(/\b(pty|ltd|inc|llc|dr\.?|prof\.?)\b/gi, "")
    .trim();

  const words = cleaned.split(/\s+/).filter(Boolean);

  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();

  // First letter of first word + first letter of last word.
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

type ProviderLogoPlaceholderProps = {
  /** Provider's display name: used to derive initials and colour. */
  providerName: string;
  /** Real logo URL from Supabase Storage. When provided, renders an <img> with initials fallback. */
  logoUrl?: string | null;
  /** Tailwind size classes for width and height. Defaults to h-16 w-16. */
  sizeClassName?: string;
  /** Tailwind text-size class. Defaults to text-xl. */
  textSizeClassName?: string;
  className?: string;
};

export function ProviderLogoPlaceholder({
  providerName,
  logoUrl,
  sizeClassName = "h-16 w-16",
  textSizeClassName = "text-xl",
  className,
}: ProviderLogoPlaceholderProps) {
  const initials = getInitials(providerName);
  const colourClasses = pickColour(providerName);

  const sharedClass = cn(
    "flex shrink-0 items-center justify-center rounded-xl border border-[var(--color-border)] shadow-sm overflow-hidden",
    sizeClassName,
    className,
  );

  if (logoUrl) {
    return (
      <div className={sharedClass} role="img" aria-label={`${providerName} logo`}>
        <Image
          src={logoUrl}
          alt=""
          width={64}
          height={64}
          className="h-full w-full object-contain"
          unoptimized
        />
      </div>
    );
  }

  return (
    <div
      role="img"
      aria-label={providerName}
      className={cn(
        sharedClass,
        "font-bold select-none",
        textSizeClassName,
        colourClasses,
      )}
    >
      {initials}
    </div>
  );
}
